import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import Button, { ButtonProps } from '@/components/content/Button'
import { borderRadius, colorSystem, fontSystem } from '@/styles/theme'
import FieldList, { FieldListType } from '@/components/content/FieldList'
import { isArray, isFinite } from 'lodash-es'
import { Icon, StackProps } from '@musinsa/mwms-ds'

interface Props {
  className?: string
  isLoading?: boolean
  buttons?: ButtonProps[]
  customFooter?: React.ReactNode
  hideToggleOnOff?: boolean
  searchDisabled?: boolean
  list: FieldListType
  gapRow?: StackProps['gap']
  onSave?: () => void
  onSearch?: () => void
  onReset?: () => void
}

const isEmpty = (value: any) => {
  if (value === 'Invalid Date' || value === 'Infinity') {
    return true
  }

  if (typeof value === 'number' && !isFinite(value)) {
    return true
  }

  // 배열인 경우 빈 배열이거나 모든 요소가 비어있는지 확인
  if (isArray(value)) {
    return value.length === 0
  }

  return !value
}

function FilterList({ className, isLoading, list, buttons, customFooter, hideToggleOnOff, searchDisabled, onSave, onSearch, onReset }: Props) {
  const [isOpened, setIsOpened] = useState(true)
  const toggleOnOff = useCallback(() => setIsOpened((prev) => !prev), [])

  const toggleOnOffButton = useMemo(
    () => (
      <ToggleOnOffButton onClick={toggleOnOff}>
        <Icon name={`chevrons-${isOpened ? 'up' : 'down'}`} size={24} color="gray600" />
        <span>검색 필터 {isOpened ? '닫기' : '열기'}</span>
      </ToggleOnOffButton>
    ),
    [toggleOnOff, isOpened],
  )

  const isRequiredNotFilled = useMemo(() => {
    return list.some((item) =>
      item.some(({ required, value, validationValue }) => {
        const comp = value as ReactElement

        if (!required || !comp) {
          return false
        }

        if (validationValue !== undefined) {
          return isEmpty(validationValue)
        }

        const triggerComp = comp.props?.trigger as ReactElement | undefined
        if (triggerComp) {
          return isArray(triggerComp.props.value) ? triggerComp.props.value.some(isEmpty) : isEmpty(triggerComp.props.value)
        }

        return isArray(comp.props.value) ? comp.props.value.some(isEmpty) : isEmpty(comp.props.value)
      }),
    )
  }, [list])

  const isShowBottom: boolean = useMemo(() => (buttons && !!buttons.length) || !!onReset || !!onSearch || hideToggleOnOff !== true, [])

  if (!isOpened) return <OffWrapper>{toggleOnOffButton}</OffWrapper>

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <FilterListWrap className={className} $isShowBottom={isShowBottom}>
        <FieldList className="field-list" list={list} />
        {isShowBottom && (
          <BottomWrapper>
            {hideToggleOnOff !== true && toggleOnOffButton}
            <ButtonWrapper>
              {customFooter}
              {buttons?.length && buttons?.map((btn, idx) => <Button key={`filter-list-${btn.content}-${idx}`} {...btn} />)}
              {onSave && (
                <Button kind="primary_dark" type="button" disabled={isLoading} onClick={onSave}>
                  저장
                </Button>
              )}
              {onReset && (
                <Button kind="secondary" type="button" disabled={isLoading} onClick={onReset}>
                  초기화
                </Button>
              )}
              {onSearch && (
                <Button kind="primary_light" type="submit" disabled={isLoading || searchDisabled || isRequiredNotFilled} onClick={onSearch}>
                  검색
                </Button>
              )}
            </ButtonWrapper>
          </BottomWrapper>
        )}
      </FilterListWrap>
    </form>
  )
}

const OffWrapper = styled.div`
  background-color: ${colorSystem.bg.filter};
  border-radius: 8px;
  display: flex;
  justify-content: center;
  position: relative;
  width: 100%;
  padding: 8px;
`

const FilterListWrap = styled.div<{ $isShowBottom: boolean }>`
  background-color: ${colorSystem.bg.filter};
  padding-bottom: ${({ $isShowBottom }) => ($isShowBottom ? '16px' : '0px')};

  .field-list {
    padding: 12px 20px;

    &.hide {
      display: none;
    }
  }

  ${borderRadius.md};
`

const BottomWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  position: relative;
  width: 100%;
  height: 36px;
`

const ButtonWrapper = styled.div`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 8px;
`

const ToggleOnOffButton = styled.div`
  color: ${colorSystem.text.common.secondary};
  ${fontSystem.Body_12_medium};
  display: flex;
  position: relative;
  cursor: pointer;
  align-items: flex-end;
  padding: 2px;

  > span {
    align-self: center;
  }
`

export default FilterList
